import React, { useContext } from "react";
import { flexRender } from "@tanstack/react-table";
import styled, { css } from "styled-components";
import { Table as BaseTable } from "components/bootstrap";
import EntityTableOverrideRow from "components/common/EntityDataTable/EntityTableOverrideRow";
import ExpandedSections from "components/common/EntityDataTable/ExpandedSections";
import {
  ACTIONS_COL_ID,
  CELL_PADDING_VERTICAL,
  CELL_PADDING_HORIZONTAL
} from "components/common/EntityDataTable/Constants";
import {
  columnOpacityVar,
  columnTransformVar,
  columnTransition,
  displayScrollRightIndicatorVar
} from "components/common/EntityDataTable/CSSVariables";
import ScrollShadow from "theme/box-shadows/ScrollShadow";
import ExpandedEntitiesSectionsContext from "./contexts/ExpandedSectionsContext";
import TableHead from "./TableHead";
const StyledTable = styled(BaseTable)(
  ({ theme }) => css`
    table-layout: fixed;
    width: 100%;
    margin-bottom: 0;
    height: 100%; // required to be able to use height: 100% in td

    tbody > tr.active {
      background-color: ${theme.colors.table.row.backgroundStriped} !important;
    }
  `
);
const Td = styled.td(
  ({ $colId, $hideEmptyTailBorder, $hidePadding, $isLastVisibleColumn, $pinningPosition, $textAlign, theme }) => css`
    word-break: break-word;
    ${$textAlign && css`
      text-align: ${$textAlign};
    `}
    opacity: var(${columnOpacityVar($colId)}, 1);
    transform: var(${columnTransformVar($colId)}, none);
    transition: var(${columnTransition()}, none);
    height: 100%; // required to be able to use height: 100% in child elements
    && {
      padding: ${CELL_PADDING_VERTICAL}px ${CELL_PADDING_HORIZONTAL}px;
    }

    ${$pinningPosition ? css`
          position: sticky;
          ${$pinningPosition === "left" ? "left" : "right"}: 0;

          ${ScrollShadow("left")}
          &::before {
            display: var(${displayScrollRightIndicatorVar}, none);
          }
        ` : ""}

    ${$hidePadding && css`
      && {
        padding: 0;
      }
    `}

    ${$hideEmptyTailBorder && css`
      &&& {
        border-right: none;
      }
    `}

    ${$isLastVisibleColumn && css`
      && {
        border-right-color: ${theme.colors.table.row.divider};
      }
    `}
  `
);
const Table = ({
  columnWidths,
  expandedSectionRenderers,
  rowOverride = void 0,
  headerGroups,
  rows
}) => {
  var _a, _b, _c;
  const { expandedSections } = useContext(ExpandedEntitiesSectionsContext);
  const isRowExpanded = (rowId) => !!(expandedSections == null ? void 0 : expandedSections[rowId]);
  const isTailColumnEmpty = !columnWidths[ACTIONS_COL_ID];
  const leafHeaders = (_b = (_a = headerGroups[headerGroups.length - 1]) == null ? void 0 : _a.headers) != null ? _b : [];
  const lastVisibleColumnId = isTailColumnEmpty ? (_c = leafHeaders[leafHeaders.length - 2]) == null ? void 0 : _c.column.id : void 0;
  return /* @__PURE__ */ React.createElement(StyledTable, { condensed: true, bordered: true }, /* @__PURE__ */ React.createElement(TableHead, { columnWidths, headerGroups }), rows.map((row) => {
    const visibleCells = row.getVisibleCells();
    const visibleCellCount = visibleCells.length;
    const renderCell = (cell) => {
      var _a2;
      const columnMeta = cell.column.columnDef.meta;
      return /* @__PURE__ */ React.createElement(
        Td,
        {
          key: cell.id,
          $colId: cell.column.id,
          $hideEmptyTailBorder: cell.column.id === ACTIONS_COL_ID && isTailColumnEmpty,
          $isLastVisibleColumn: cell.column.id === lastVisibleColumnId,
          $pinningPosition: cell.column.getIsPinned(),
          $hidePadding: columnMeta == null ? void 0 : columnMeta.hideCellPadding,
          $textAlign: (_a2 = columnMeta == null ? void 0 : columnMeta.columnRenderer) == null ? void 0 : _a2.textAlign
        },
        flexRender(cell.column.columnDef.cell, cell.getContext())
      );
    };
    const actionCellIndex = visibleCells.findIndex((cell) => cell.column.id === ACTIONS_COL_ID);
    const defaultRowActionCell = actionCellIndex >= 0 ? renderCell(visibleCells[actionCellIndex]) : void 0;
    const overrideNotice = rowOverride == null ? void 0 : rowOverride(row.original);
    return /* @__PURE__ */ React.createElement("tbody", { key: `table-row-${row.id}`, "data-testid": `table-row-${row.id}` }, overrideNotice !== void 0 ? /* @__PURE__ */ React.createElement(
      EntityTableOverrideRow,
      {
        visibleCellCount,
        notice: overrideNotice,
        actionCell: defaultRowActionCell
      }
    ) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("tr", { className: isRowExpanded(row.id) ? "active" : null }, visibleCells.map(renderCell)), /* @__PURE__ */ React.createElement(
      ExpandedSections,
      {
        key: `expanded-sections-${row.id}`,
        expandedSectionRenderers,
        entity: row.original,
        visibleCellCount
      }
    )));
  }));
};
export default React.memo(Table);
